import { memo, useCallback, useMemo } from 'react';
import { useDeleteThreadsChannel } from '../../api/delete-threads-channel.api';
import { GET_THREADS_CHANNELS_QUERY_KEY } from '../../api/get-threads-channels.api';
import { axiosExtractErrorMessage } from '@/lib/axios';
import { useCustomToast } from '@/hooks/use-custom-toast';
import { queryClient } from '@/lib/react-query';
import { useChannelModalRootActionsStore } from '@/features/channels/_shared/components/channel-modal-root/channel-modal-root.store';
import ModalWithConfirmation from '@/components/_ui/modal-with-confirmation/modal-with-confirmation';

export type TDeleteThreadsChannelModalType = 'DeleteThreadsChannelModal';
export interface IDeleteThreadsChannelModalProps {
  organizationChannelId: string;
}

const TOAST_MESSAGES_DELETE_THREADS_CHANNEL = {
  success: 'The channel integration has been successfully deleted!',
  error: 'Failed to delete Threads integration!',
};

const DeleteThreadsChannelModal = memo(({ organizationChannelId }: IDeleteThreadsChannelModalProps) => {
  const channelModalRootActions = useChannelModalRootActionsStore();
  const showToast = useCustomToast();

  const isOpenDeleteThreadsChannelModal = useMemo(() => {
    return channelModalRootActions.checkIsOpenModal('DeleteThreadsChannelModal');
  }, [channelModalRootActions]);

  const mutationDeleteThreadsChannel = useDeleteThreadsChannel({
    mutationConfig: {
      onError: (error) => {
        const errorMessage = axiosExtractErrorMessage(error) || TOAST_MESSAGES_DELETE_THREADS_CHANNEL.error;
        showToast({ type: 'error', message: errorMessage });
      },
      onSuccess: () => {
        Promise.all([queryClient.invalidateQueries({ queryKey: [GET_THREADS_CHANNELS_QUERY_KEY] })]);
        channelModalRootActions.closeModal('DeleteThreadsChannelModal');
        showToast({ type: 'success', message: TOAST_MESSAGES_DELETE_THREADS_CHANNEL.success });
      },
    },
  });

  const handleDelete = useCallback(() => {
    if (organizationChannelId) {
      mutationDeleteThreadsChannel.mutate({
        organizationChannelId: organizationChannelId || '',
      });
    }
  }, [organizationChannelId, mutationDeleteThreadsChannel]);

  const handleClose = useCallback(() => {
    channelModalRootActions.closeModal('DeleteThreadsChannelModal');
  }, [channelModalRootActions]);

  return (
    <ModalWithConfirmation
      isOpen={isOpenDeleteThreadsChannelModal}
      onClose={handleClose}
      onDelete={handleDelete}
      isLoading={mutationDeleteThreadsChannel.isPending}
      title="Delete Integration Threads"
      description="Do you want to delete this integration?"
      bannerTitle="Deleting this channel will permanently delete all related data, including chat history and analytics."
      confirmText="delete my channel"
    />
  );
});

DeleteThreadsChannelModal.displayName = 'DeleteThreadsChannelModal';

export default DeleteThreadsChannelModal;
